import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { portalApi } from '@/api/portal'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { CertificateViewerModal } from '@/components/shared/CertificateViewerModal'
import { ArrowLeft, Eye, Download, ShieldCheck } from 'lucide-react'

export default function StaffTrainingView() {
  const { staffId } = useParams<{ staffId: string }>()
  const navigate = useNavigate()
  const [viewerUrl, setViewerUrl] = useState<string | null>(null)
  const [viewerContentType, setViewerContentType] = useState('application/pdf')
  const [viewerTitle, setViewerTitle] = useState('')

  const { data: team = [], isLoading } = useQuery({
    queryKey: ['team-training-records'],
    queryFn: () => portalApi.getTeamRecords().then(r => r.data),
  })

  const member = team.find(m => m.staffMemberId === staffId)

  const handleView = async (recordId: string, courseName: string) => {
    const res = await portalApi.streamTeamCertificate(recordId)
    const blob: Blob = res.data
    const blobUrl = URL.createObjectURL(blob)
    setViewerUrl(prev => { if (prev) URL.revokeObjectURL(prev); return blobUrl })
    setViewerContentType(blob.type || 'application/octet-stream')
    setViewerTitle(courseName)
  }

  const handleDownload = async (recordId: string) => {
    const res = await portalApi.streamTeamCertificate(recordId)
    const blobUrl = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = 'certificate'
    a.click()
    URL.revokeObjectURL(blobUrl)
  }

  if (isLoading) return <p className="text-gray-500">Loading…</p>

  if (!member) return (
    <div>
      <Button variant="ghost" size="sm" className="gap-2 mb-4 -ml-2" onClick={() => navigate('/portal/team')}>
        <ArrowLeft size={16} /> Back to team
      </Button>
      <p className="text-gray-500">Staff member not found.</p>
    </div>
  )

  return (
    <>
      {viewerUrl && (
        <CertificateViewerModal
          blobUrl={viewerUrl}
          contentType={viewerContentType}
          title={viewerTitle}
          onClose={() => {
            URL.revokeObjectURL(viewerUrl)
            setViewerUrl(null)
          }}
        />
      )}

      <div>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 mb-6 -ml-2 text-gray-500 hover:text-gray-900 cursor-pointer"
          onClick={() => navigate('/portal/team')}
        >
          <ArrowLeft size={16} /> Back to team
        </Button>

        <h2 className="text-2xl font-semibold text-gray-900 mb-1">{member.fullName}</h2>
        <p className="text-gray-500 text-sm mb-6">{member.email}</p>

        {member.records.length === 0 ? (
          <Card>
            <p className="text-center text-gray-500 py-8 text-sm">No training records on file.</p>
          </Card>
        ) : (
          <>
            {/* Mobile: card list */}
            <div className="sm:hidden space-y-3">
              {member.records.map(record => (
                <div key={record.id} className="bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium text-gray-900">{record.courseName}</p>
                    <StatusBadge status={record.status} />
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5">
                    Completed {record.completedDate.slice(0, 10)}
                    {record.expiryDate && <> · Expires {record.expiryDate.slice(0, 10)}</>}
                  </p>
                  {record.hasCertificate && (
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => handleView(record.id, record.courseName)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <Eye size={13} /> View
                      </button>
                      <button
                        onClick={() => handleDownload(record.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <Download size={13} /> Download
                      </button>
                      {record.courseIsThirdPartyCertified && record.certificateId && (
                        <a
                          href={`/verify/${record.certificateId}`}
                          target="_blank" rel="noreferrer"
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <ShieldCheck size={13} /> Verify
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop: table */}
            <Card className="hidden sm:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Course</TableHead>
                    <TableHead>Completed</TableHead>
                    <TableHead>Expiry</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {member.records.map(record => (
                    <TableRow key={record.id}>
                      <TableCell className="font-medium">{record.courseName}</TableCell>
                      <TableCell className="text-gray-600">{record.completedDate.slice(0, 10)}</TableCell>
                      <TableCell className="text-gray-600">{record.expiryDate ? record.expiryDate.slice(0, 10) : '—'}</TableCell>
                      <TableCell><StatusBadge status={record.status} /></TableCell>
                      <TableCell>
                        {record.hasCertificate && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 gap-1.5 text-slate-500"
                              onClick={() => handleView(record.id, record.courseName)}
                            >
                              <Eye size={14} /> View
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-slate-400 hover:text-slate-700"
                              title="Download certificate"
                              onClick={() => handleDownload(record.id)}
                            >
                              <Download size={14} />
                            </Button>
                            {record.courseIsThirdPartyCertified && record.certificateId && (
                              <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-slate-500" asChild>
                                <a href={`/verify/${record.certificateId}`} target="_blank" rel="noreferrer" title="Open the public verification page">
                                  <ShieldCheck size={14} /> Verify
                                </a>
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </>
        )}
      </div>
    </>
  )
}
